import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock } from "@phosphor-icons/react";

const DAYS = [
  { i: 0, label: "Mon" },
  { i: 1, label: "Tue" },
  { i: 2, label: "Wed" },
  { i: 3, label: "Thu" },
  { i: 4, label: "Fri" },
  { i: 5, label: "Sat" },
  { i: 6, label: "Sun" },
];

const COMMON_TZ = [
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Australia/Sydney",
  "Australia/Perth",
  "Asia/Singapore",
  "Asia/Kolkata",
];

export function BusinessHoursPanel({ settings, setSettings, isAdmin }) {
  const enabled = !!settings.business_hours_enabled;
  const days = Array.isArray(settings.business_days) ? settings.business_days : [0, 1, 2, 3, 4];
  const toggleDay = (i) => {
    if (!isAdmin) return;
    setSettings((s) => {
      const cur = new Set(s.business_days || []);
      if (cur.has(i)) cur.delete(i); else cur.add(i);
      return { ...s, business_days: Array.from(cur).sort((a, b) => a - b) };
    });
  };

  return (
    <div className="hairline rounded-lg p-4 bg-white space-y-3" data-testid="settings-business-hours-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={18} weight="duotone" className="text-[#4f4c90]" />
          <div>
            <h3 className="font-heading font-semibold text-lg">Business hours & SLA</h3>
            <p className="text-xs text-slate-500">Resolution time metrics and SLA due dates only count work hours.</p>
          </div>
        </div>
        <Switch
          data-testid="settings-bh-enabled"
          checked={enabled}
          onCheckedChange={(v) => isAdmin && setSettings((s) => ({ ...s, business_hours_enabled: v }))}
          disabled={!isAdmin}
        />
      </div>

      {enabled && (
        <div className="pt-2 border-t border-slate-200 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Start</Label>
              <Input
                type="time"
                data-testid="settings-bh-start"
                value={settings.business_hours_start || "08:00"}
                onChange={(e) => isAdmin && setSettings((s) => ({ ...s, business_hours_start: e.target.value }))}
                disabled={!isAdmin}
                className="mt-1 h-9 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500 tracking-wider">End</Label>
              <Input
                type="time"
                data-testid="settings-bh-end"
                value={settings.business_hours_end || "17:00"}
                onChange={(e) => isAdmin && setSettings((s) => ({ ...s, business_hours_end: e.target.value }))}
                disabled={!isAdmin}
                className="mt-1 h-9 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Timezone</Label>
              <Select
                value={settings.business_timezone || "Europe/London"}
                onValueChange={(v) => isAdmin && setSettings((s) => ({ ...s, business_timezone: v }))}
                disabled={!isAdmin}
              >
                <SelectTrigger data-testid="settings-bh-tz" className="mt-1 h-9 bg-white border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-60">
                  {COMMON_TZ.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Working days</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = days.includes(d.i);
                return (
                  <button
                    key={d.i}
                    type="button"
                    onClick={() => toggleDay(d.i)}
                    disabled={!isAdmin}
                    data-testid={`settings-bh-day-${d.i}`}
                    className={[
                      "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                      on
                        ? "bg-[#4f4c90] text-white border-[#4f4c90]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
                      !isAdmin && "opacity-60 cursor-not-allowed",
                    ].filter(Boolean).join(" ")}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 font-mono border border-slate-200 rounded-md bg-slate-50 px-3 py-2">
            Example — with these settings, an urgent ticket created Friday at 4:30 PM has a resolution due of
            {" "}<span className="font-semibold text-slate-700">Monday 4:30 PM {settings.business_timezone || "Europe/London"}</span>{" "}
            instead of Saturday morning. Remember to click <span className="font-semibold text-slate-700">Save settings</span> after changing.
          </div>
        </div>
      )}
    </div>
  );
}
